import { useEffect, useRef, useState } from 'react';
import { Alert, Autocomplete, Box, Button, CircularProgress, TextField } from '@mui/material';
import { apiService, type CampaignListItemDto } from '@shared/services/apiService';

export const CampaignPicker = ({
  value,
  onChange,
  disabled = false,
  compact = false,
}: {
  value: Pick<CampaignListItemDto, 'id' | 'title'> | null;
  onChange: (value: CampaignListItemDto | null) => void;
  disabled?: boolean;
  compact?: boolean;
}) => {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<CampaignListItemDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);
  const [fetch, { isFetching }] =
    apiService.endpoints.campaignListPaginatedControllerGetCampaignListPaginated.useLazyQuery();
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    const own = ++generation.current;
    setRows([]);
    setCursor(null);
    setHasMore(false);
    setFailed(false);
    const request = fetch({
      limit: 50,
      sortBy: 'title',
      sortOrder: 'ASC',
      search: query || undefined,
    });
    request
      .unwrap()
      .then((page) => {
        if (generation.current !== own) return;
        setRows(page.data);
        setCursor(page.meta.cursor);
        setHasMore(page.meta.hasMore);
      })
      .catch(() => {
        if (generation.current === own) setFailed(true);
      });
    return () => {
      generation.current = own + 1;
      request.abort();
    };
  }, [fetch, query, retry]);
  const more = async () => {
    if (!cursor || isFetching) return;
    const own = generation.current;
    try {
      const page = await fetch({
        limit: 50,
        sortBy: 'title',
        sortOrder: 'ASC',
        search: query || undefined,
        cursor,
      }).unwrap();
      if (generation.current !== own) return;
      setRows((previous) =>
        Array.from(new Map([...previous, ...page.data].map((row) => [row.id, row])).values()),
      );
      setCursor(page.meta.cursor);
      setHasMore(page.meta.hasMore);
      setFailed(false);
    } catch {
      if (generation.current === own) setFailed(true);
    }
  };
  return (
    <Box sx={{ mb: compact ? 0 : 3 }}>
      <Autocomplete<Pick<CampaignListItemDto, 'id' | 'title'>>
        size={compact ? 'small' : 'medium'}
        options={rows}
        value={value}
        disabled={disabled}
        loading={isFetching}
        filterOptions={(items) => items}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionLabel={(row) => row.title}
        onChange={(_, row) =>
          onChange(row ? (rows.find((item) => item.id === row.id) ?? null) : null)
        }
        onInputChange={(_, text, reason) => {
          if (reason === 'input' || reason === 'clear') setSearch(text);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Campaign"
            placeholder="Search campaigns"
            helperText={
              compact ? undefined : 'Resources and changes belong to the selected campaign.'
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {hasMore && (
        <Button onClick={() => void more()} disabled={disabled || isFetching}>
          Load more campaigns
        </Button>
      )}
      {failed && (
        <Alert
          severity="error"
          action={<Button onClick={() => setRetry((n) => n + 1)}>Retry</Button>}>
          Unable to load campaigns.
        </Alert>
      )}
    </Box>
  );
};
